import { Injectable } from "@angular/core";
import { EndsStepModel } from "../wizard/steps/1-ends/ends-step.model";
import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import {
    DeviceData,
    EndData,
    EndsData,
    MuxDemuxData,
    PortData,
    SlotData
} from "../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import {
    TransponderPort,
    TransponderPortData,
    TranspondersData,
    TranspondersStepModel
} from "../wizard/steps/2-transponders/transponders-step.model";
import { SelectOption } from "@nms-angular-toolkit/nms-select";
import {
    IntermediateDevicesValidatorData,
    IntermediatesStepModel
} from "../wizard/steps/3-intermediates/intermediates-step.model";
import { LinksStepModel, LinksValidatorData } from "../wizard/steps/4-links/links-step.model";
import { AmplifiersStepModel, AmplifiersValidatorData } from "../wizard/steps/5-amplifiers/amplifiers-step.model";
import {
    OscManagementStepModel,
    OscsValidatorData,
    OscValidatorData
} from "../wizard/steps/6-osc-management/osc-management-step.model";

/**
 * Classe utilitária responsável por tratar os dados da validação do circuito DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmCircuitWizardValidationUtilsService {

    buildEndsData(endsStepModel: EndsStepModel) {
        const aEndSlotSelected = this.getSlotsWithSelectedPorts(endsStepModel.aEndSlots, endsStepModel.aEndPorts);
        const zEndSlotSelected = this.getSlotsWithSelectedPorts(endsStepModel.zEndSlots, endsStepModel.zEndPorts);
        const aEndDeviceData = this.getEndDeviceData(endsStepModel.aEndDevice);
        const zEndDeviceData = this.getEndDeviceData(endsStepModel.zEndDevice);
        const aEndData = this.getEndData(aEndDeviceData, aEndSlotSelected, endsStepModel.aEndMux, endsStepModel.aEndHasMux);
        const zEndData = this.getEndData(zEndDeviceData, zEndSlotSelected, endsStepModel.zEndMux, endsStepModel.zEndHasMux);

        return {
            aEnd: aEndData,
            zEnd: zEndData,
            circuitId: endsStepModel.circuitId
        } as EndsData;
    }

    getDeviceSlotPortError(endsStepModel: EndsStepModel): string {
        const { aEndDevice, zEndDevice, aEndSlots, aEndPorts, zEndSlots, zEndPorts } = endsStepModel;
        const errorConditions = [
            { condition: !aEndDevice || !zEndDevice },
            { condition: aEndSlots.length === 0 },
            { condition: aEndPorts.length === 0 },
            { condition: zEndSlots.length === 0 },
            { condition: zEndPorts.length === 0 }
        ];
        const hasError = errorConditions.some(check => check.condition);

        return hasError ? "dwdm.circuit.wizard.ends.step.validator.error" : null;
    }

    buildTranspondersData(transpondersModel: TranspondersStepModel, endsStepModel: EndsStepModel,
        channelOptions: Array<SelectOption>): TranspondersData {
        return {
            circuitId: transpondersModel.circuitId,
            aEndDevice: transpondersModel.aEndDevice,
            aEndUseMuxDemux: endsStepModel.aEndHasMux,
            aEndMuxDemux: endsStepModel.aEndMux,
            zEndDevice: transpondersModel.zEndDevice,
            zEndUseMuxDemux: endsStepModel.zEndHasMux,
            zEndMuxDemux: endsStepModel.zEndMux,
            aEndPorts: this.convertTranspondersPort(transpondersModel.aEndPorts, channelOptions),
            zEndPorts: this.convertTranspondersPort(transpondersModel.zEndPorts, channelOptions)
        };
    }

    buildIntermediatesData(intermediatesModel: IntermediatesStepModel, aEndResourceId: number, zEndResourceId: number)
        : IntermediateDevicesValidatorData {

        return {
            aEndResourceId,
            zEndResourceId,
            resourceIds: intermediatesModel.devices.map(device => device.resourceId)
        };
    }

    buildLinksData(linksModel: LinksStepModel): LinksValidatorData {
        return {
            linkIds: linksModel.links.map(link => link.id),
            intermediatesResourceIds: linksModel.intermediatesResourceIds,
            aEndResourceId: linksModel.aEndResourceId,
            zEndResourceId: linksModel.zEndResourceId,
            aEndMuxResourceId: linksModel.aEndMuxResourceId,
            zEndMuxResourceId: linksModel.zEndMuxResourceId,
        };
    }

    buildAmplifiersData(model: AmplifiersStepModel): AmplifiersValidatorData {
        const amplifiers = model.configuredAmplifiers.map(amplifier => {
            const { portIdentifier, limit, losAction, losThreshold, mode, profile, value } = amplifier;

            return {
                portIdentifier,
                limit,
                losAction,
                losThreshold,
                mode,
                profile,
                value
            }
        });

        return { amplifiers };
    }

    buildOscsData(model: OscManagementStepModel, endsStepModel: EndsStepModel): OscsValidatorData {
        const oscs: Array<OscValidatorData> = model.oscManagementPorts.map(osc => {
            return {
                deviceResourceId: osc.endId,
                linkRxId: osc.linkRxId,
                linkTxId: osc.linkTxId,
                chassis: osc.oscChassisIdentifier,
                slot: osc.oscSlotIdentifier,
                portNo: osc.oscPort,
                portModelIdentifier: osc.oscPortModelIdentifier
            }
        });

        return {
            oscs,
            aEndResourceId: endsStepModel.aEndDevice.id,
            zEndResourceId: endsStepModel.zEndDevice.id
        };
    }

    private convertTranspondersPort(transponderPort: TransponderPort[],
            channelOptions: Array<SelectOption>): TransponderPortData[]  {
        return transponderPort.map(port => this.createTransponderPortData(port, channelOptions));
    }

    private createTransponderPortData(port: TransponderPort, channelOptions: Array<SelectOption>): TransponderPortData {
        const channel = channelOptions.find(channelOption => channelOption.label === port.channel);
        return {
            portNo: port.portNo,
            portModel: port.portModel,
            portName: port.portName,
            channel: channel.id,
            multirate: port.multirate,
            txPower: port.txPower?.toString()
        } as TransponderPortData;
    }

    private getEndDeviceData(device: Device) {
        return {
            resourceId: device.id,
            name: device.name,
            model: device.model,
            modelCode: device.modelCode,
            vendorCode: device.vendorCode,
            locationId: device.locationId
        } as DeviceData;
    }

    private getEndData(deviceData: DeviceData, slotData: SlotData[], muxDemuxData: MuxDemuxData, useMuxDemux: boolean) {
        return {
            deviceData,
            slotData,
            muxDemuxData,
            useMuxDemux
        } as EndData;
    }

    private getSlotsWithSelectedPorts(slots: SlotData[], selectedPorts: PortData[]): SlotData[] {
        return slots.map(slot => {
            const filteredPorts = slot.ports.filter(port =>
                selectedPorts.some(selected => this.isPortSelected(port, selected)));

            return {
                slotNo: slot.slotNo,
                chassisNo: slot.chassisNo,
                slotModel: slot.slotModel,
                slotName: slot.slotName,
                ports: filteredPorts
            };
        });
    }

    private isPortSelected(port: PortData, selectedPort: PortData): boolean {
        return port.portNo === selectedPort.portNo && port.portName === selectedPort.portName;
    }
}
